/**
 * Single source of truth for product locales.
 * Imported by proxy, next.config, i18n index, and API helpers.
 * Keep this file free of React / dictionary imports so next.config can load it.
 * Only type-only imports of dictionaries are allowed (erased at runtime).
 */

import type en from "./en";

// NOTA orden vs default (2026-09-06): `es` va primero por conveniencia de
// lectura/edición, NO indica default. El default (unprefixed, localePrefix
// "as-needed") es `en` vía DEFAULT_LOCALE abajo.
export const SUPPORTED_LOCALES = [
  "es",
  "en",
  "pt",
  "fr",
  "de",
  "it",
  "nl",
  "pl",
  "ru",
  "ja",
  "zh",
  "ko",
  "tr",
  "ar",
] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

/** Product default (unprefixed with localePrefix "as-needed"). */
export const DEFAULT_LOCALE: Locale = "en";

/** Next.js / app locale prefix strategy. */
export const LOCALE_PREFIX = "as-needed" as const;

export function isSupportedLocale(
  value: string | null | undefined,
): value is Locale {
  return (
    typeof value === "string" &&
    (SUPPORTED_LOCALES as readonly string[]).includes(value)
  );
}

export type TranslationKey = keyof typeof en | (string & {});

export const LOCALE_META: Record<
  Locale,
  {
    nameEn: string;
    nameNative: string;
    dir: "ltr" | "rtl";
    intl: string;
    og: string;
    /** Public path to the language flag SVG under /public. */
    flagSrc: string;
    /** Accessible short label for the flag (country representing the language). */
    flagAlt: string;
  }
> = {
  es: {
    nameEn: "Spanish",
    nameNative: "Español",
    dir: "ltr",
    intl: "es-ES",
    og: "es_ES",
    flagSrc: "/flags/es.svg",
    flagAlt: "España",
  },
  en: {
    nameEn: "English",
    nameNative: "English",
    dir: "ltr",
    intl: "en-US",
    og: "en_US",
    flagSrc: "/flags/en.svg",
    flagAlt: "United States",
  },
  pt: {
    nameEn: "Portuguese",
    nameNative: "Português",
    dir: "ltr",
    intl: "pt-BR",
    og: "pt_BR",
    flagSrc: "/flags/pt.svg",
    flagAlt: "Brasil",
  },
  fr: {
    nameEn: "French",
    nameNative: "Français",
    dir: "ltr",
    intl: "fr-FR",
    og: "fr_FR",
    flagSrc: "/flags/fr.svg",
    flagAlt: "France",
  },
  de: {
    nameEn: "German",
    nameNative: "Deutsch",
    dir: "ltr",
    intl: "de-DE",
    og: "de_DE",
    flagSrc: "/flags/de.svg",
    flagAlt: "Deutschland",
  },
  it: {
    nameEn: "Italian",
    nameNative: "Italiano",
    dir: "ltr",
    intl: "it-IT",
    og: "it_IT",
    flagSrc: "/flags/it.svg",
    flagAlt: "Italia",
  },
  nl: {
    nameEn: "Dutch",
    nameNative: "Nederlands",
    dir: "ltr",
    intl: "nl-NL",
    og: "nl_NL",
    flagSrc: "/flags/nl.svg",
    flagAlt: "Nederland",
  },
  pl: {
    nameEn: "Polish",
    nameNative: "Polski",
    dir: "ltr",
    intl: "pl-PL",
    og: "pl_PL",
    flagSrc: "/flags/pl.svg",
    flagAlt: "Polska",
  },
  ru: {
    nameEn: "Russian",
    nameNative: "Русский",
    dir: "ltr",
    intl: "ru-RU",
    og: "ru_RU",
    flagSrc: "/flags/ru.svg",
    flagAlt: "Россия",
  },
  ja: {
    nameEn: "Japanese",
    nameNative: "日本語",
    dir: "ltr",
    intl: "ja-JP",
    og: "ja_JP",
    flagSrc: "/flags/ja.svg",
    flagAlt: "日本",
  },
  zh: {
    nameEn: "Chinese",
    nameNative: "中文",
    dir: "ltr",
    intl: "zh-CN",
    og: "zh_CN",
    flagSrc: "/flags/zh.svg",
    flagAlt: "中国",
  },
  ko: {
    nameEn: "Korean",
    nameNative: "한국어",
    dir: "ltr",
    intl: "ko-KR",
    og: "ko_KR",
    flagSrc: "/flags/ko.svg",
    flagAlt: "대한민국",
  },
  tr: {
    nameEn: "Turkish",
    nameNative: "Türkçe",
    dir: "ltr",
    intl: "tr-TR",
    og: "tr_TR",
    flagSrc: "/flags/tr.svg",
    flagAlt: "Türkiye",
  },
  ar: {
    nameEn: "Arabic",
    nameNative: "العربية",
    dir: "rtl",
    intl: "ar-SA",
    og: "ar_SA",
    flagSrc: "/flags/ar.svg",
    flagAlt: "السعودية",
  },
};

const localeMap: Record<string, Locale> = {
  es: "es",
  en: "en",
  pt: "pt",
  fr: "fr",
  de: "de",
  it: "it",
  nl: "nl",
  pl: "pl",
  ru: "ru",
  ja: "ja",
  zh: "zh",
  ko: "ko",
  tr: "tr",
  ar: "ar",
};

export function detectLocaleFromLanguages(
  languages: readonly string[],
): Locale {
  for (const raw of languages) {
    const normalized = raw.toLowerCase();
    const primary = normalized.split("-")[0];
    /* v8 ignore next -- residual branch */
    const locale = localeMap[primary ?? ""];
    if (locale) return locale;
  }
  return DEFAULT_LOCALE;
}

export function localeToIntl(locale: Locale): string {
  return LOCALE_META[locale]?.intl ?? "en-US";
}

/**
 * Returns the OpenGraph locale code (e.g. "es_ES", "fr_FR") for a given
 * locale. Falls back to "en_US" for unknown locales so OG tags never get
 * a wrong/random language code.
 */
export function localeToOpenGraph(locale: Locale): string {
  return LOCALE_META[locale]?.og ?? "en_US";
}

export function localeDirection(locale: Locale): "ltr" | "rtl" {
  return LOCALE_META[locale]?.dir ?? "ltr";
}

/**
 * Human-readable language label for instructions sent to the model, e.g.
 * "Spanish (Español)" or "Japanese (日本語)". A bare ISO code ("es") is a weak
 * instruction — models can echo a map's English skill example instead of the
 * user's language — while the English name plus the native name is
 * unambiguous. Locales whose two names coincide (English) stay single-form.
 * Pure and dependency-free so the proxy/next.config import stays safe.
 */
export function localeToLanguageLabel(locale: Locale): string {
  const meta = LOCALE_META[locale];
  /* v8 ignore next -- unreachable for the Locale union; guard for JS callers */
  if (!meta) return locale;
  return meta.nameNative === meta.nameEn
    ? meta.nameEn
    : `${meta.nameEn} (${meta.nameNative})`;
}

const NON_DEFAULT_LOCALES_REGEX = new RegExp(
  `^\\/(${SUPPORTED_LOCALES.filter((l) => l !== DEFAULT_LOCALE).join("|")})(?=\\/|$)`,
);

/**
 * Strip a leading locale prefix from a pathname.
 * English (default) has no prefix.
 */
export function stripLocalePrefix(pathname: string): string {
  const cleaned = pathname.startsWith("/") ? pathname : `/${pathname}`;
  /* v8 ignore next 2 -- residual branch */
  const stripped = cleaned.replace(NON_DEFAULT_LOCALES_REGEX, "") || "/";
  /* v8 ignore next -- residual branch */
  return stripped === "" ? "/" : stripped;
}

/**
 * Build a locale-aware path. English is unprefixed; every other supported locale uses `/{locale}/...`.
 * Home is `/` for English and `/{locale}` for others.
 */
export function localePath(locale: Locale, path: string = "/"): string {
  // FASE 6 oferta-9 L1 (wire): path no-string → "/" (nunca throw en metadata).
  const safePath = normalizeLocaleInputPath(path);
  /* v8 ignore next -- residual branch */
  const base = stripLocalePrefix(safePath === "" ? "/" : safePath);
  if (locale === DEFAULT_LOCALE) return base;
  if (base === "/") return `/${locale}`;
  return `/${locale}${base}`;
}

// ---------------------------------------------------------------------------
// FASE 6 competencia oferta-9 LOCALES (2026-09-28, SEED=2026-09-28-bae4824d).
// Dos mecánicas compartidas con seo (la 2ª vive en seo/public-urls.ts).
// Research 2026:
// - better-i18n 2026-03-01 hreflang-locale-urls (mismo contenido en /en/ y /
//   sin canónica diluye ranking) + thestacc 2026-07-10 multilingual-seo
//   (31% hreflang en conflicto; canónica self-referencing) — ver websearch.
// - DEV 2026-06-17 React i18n (RTL + locale-routing + SEO metadata por locale).
// ---------------------------------------------------------------------------

/**
 * [L1 locale-path-nonstring-safe-slash] Sanea el path de entrada a string con
 * leading slash; no-string/vacío → "/". V2 medible vs excluidos:
 * stripLocalePrefix asumía string (un null runtime desde metadata lanzaba en
 * .startsWith y rompía el build de la página). Puro, sin copy.
 * Fingerprint `locale-path-nonstring-safe-slash`.
 */
export function normalizeLocaleInputPath(path: unknown): string {
  if (typeof path !== "string" || path.length === 0) return "/";
  const leading = path.startsWith("/") ? path : `/${path}`;
  // FASE 4 MEJORA MASIVA v2 oferta-9 T3 (2026-09-28, SEED=2026-09-28-bae4824d;
  // research 2026 ai-glot "multilingual SEO 2026" + clickrank.ai 2026
  // "canonical within language; double-slash duplicates dilute signal"):
  // stripLocalePrefix no colapsa dobles — localePath("es", "//pricing")
  // emitía `/es//pricing` (canónica + hreflang duplicadas indexables).
  // Ahora: colapsa `/{2,}` (paridad normalizeCanonicalPath en seo). Sanos
  // intactos (sin dobles → identidad); L1 intacto (no-string/vacío → "/").
  // Fingerprint `locale-path-double-slash-collapse`.
  return leading.replace(/\/{2,}/g, "/");
}

/**
 * Relative canonical path for metadata (respects metadataBase).
 * Aligns with hreflang alternates so supported locales are not collapsed to ES.
 */
export function localeCanonical(locale: Locale, path: string = "/"): string {
  return localePath(locale, path);
}
